import { inject, Injectable, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Attachment, Channel, Doddle, SubscriblyPost } from '../models';
import { EPostState } from '../enums';
import { WorkspaceService } from './workspace.service';
import { removeEmptyAttributes } from '../utilities';
import { ActivatedRoute, Router } from '@angular/router';
import { PostService } from './post.service';
import { Observable, of } from 'rxjs';
import { PostApiService } from '../api';
import { switchMap, tap } from 'rxjs/operators';
import { isValid } from 'date-fns';

@Injectable({
  providedIn: 'root',
})
export class EditorDialogService {
  formBuilder = inject(FormBuilder);
  workspaceService = inject(WorkspaceService);
  postService = inject(PostService);
  postApiService = inject(PostApiService);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);

  // Signals for state management
  doddle = signal<Doddle | null>(null);
  doddleForm!: FormGroup;
  posts = signal<FormGroup[]>([]);
  isEditMode = signal(false);
  activeChannel = signal<Channel | null>(null);
  isLoading = signal(false);

  private isFirstUpdate = signal(true);

  // Add a new signal for publish date
  currentPublishDate = signal<Date | null>(null);

  constructor() {
    this.initDoddleForm();
  }

  initDoddleForm() {
    this.doddleForm = this.formBuilder.group(
      {
        title: [null],
        content: [''],
        publishDate: [null],
        attachments: [[]],
        channels: [[]],
        status: [EPostState.draft],
      },
      { updateOn: 'blur' },
    );

    // Subscribe to publish date changes
    this.publishDate.valueChanges.subscribe((date) => {
      if (date) {
        this.currentPublishDate.set(new Date(date));
      }
    });
  }

  loadDoddleData(doddle: Doddle): void {
    this.isLoading.set(true);
    this.doddle.set(doddle);

    this.doddleForm.patchValue({
      title: doddle.title || null,
      content: doddle.content || '',
      publishDate: doddle.publishAt ? new Date(doddle.publishAt) : null,
      attachments: doddle.mediaItems || [],
      channels: doddle.channels?.map((channel) => channel.id) ?? [],
      status: doddle.status,
    });

    this.isLoading.set(false);
  }

  // Getters for form controls
  get title(): FormControl {
    return this.doddleForm.get('title') as FormControl;
  }

  get content(): FormControl {
    return this.doddleForm.get('content') as FormControl;
  }

  get publishDate(): FormControl {
    return this.doddleForm.get('publishDate') as FormControl;
  }

  get attachments(): FormControl {
    return this.doddleForm.get('attachments') as FormControl;
  }

  get channels(): FormControl {
    return this.doddleForm.get('channels') as FormControl;
  }

  get status(): FormControl {
    return this.doddleForm.get('status') as FormControl;
  }

  get doddlePayload(): Partial<Doddle> {
    const mediaIds = this.attachments.value.map(
      (media: Attachment) => media.id,
    );

    const payload: Partial<Doddle> = {
      title: this.title.value,
      content: this.content.value,
      status: this.status.value,
      mediaIds: mediaIds,
      workspaceId: this.workspaceService.activeWorkspace().id,
      publishAt:
        this.publishDate.value &&
        new Date(this.publishDate.value).toISOString(),
    };

    return removeEmptyAttributes(payload);
  }

  createDoddle(): Observable<Doddle> {
    const payload = this.doddlePayload;
    return this.postService.createDoddle(payload).pipe(
      tap((doddle) => {
        this.doddle.set(doddle);
      }),
    );
  }

  deleteDoddle(doddle: Doddle): Observable<Doddle> {
    return this.postService.deleteDoddle(doddle.id);
  }

  deleteAttachment(attachment: Attachment): void {
    const newAttachments = this.attachments.value.filter(
      (item: Attachment) => item.id !== attachment.id,
    );
    this.attachments.setValue(newAttachments);
  }

  setPublishDate(date: Date) {
    this.currentPublishDate.set(date);

    const doddleId = this.doddle()?.id;
    if (doddleId) {
      this.postService
        .updateDoddle(
          doddleId,
          {
            publishAt: date.toISOString(),
          },
          true,
        )
        .subscribe({
          next: () => {
            this.publishDate.setValue(date, { emitEvent: false });
          },
          error: (error) => {
            console.error('Error updating publish date:', error);
          },
        });
    } else {
      this.publishDate.setValue(date, { emitEvent: false });
    }
  }

  updateDoddle(doddleId: string): Observable<Doddle> {
    const payload = this.doddlePayload;
    const { workspaceId, ...payloadWithoutWorkspaceId } = payload;

    return this.postService
      .updateDoddle(doddleId, payloadWithoutWorkspaceId)
      .pipe(
        switchMap((doddle) => {
          // If it's the first update, set the status to draft and update the doddle.
          // PatchPosts flag set to true will update all posts at the same time.
          if (this.isFirstUpdate()) {
            this.isFirstUpdate.set(false);
            this.status.setValue(EPostState.draft, { emitEvent: false });
            this.changeAllPostsFormStatus(EPostState.draft);

            return this.postService.updateDoddle(
              this.doddle()!.id,
              { status: this.status.value },
              true,
            );
          }
          return of(doddle);
        }),
        tap((doddle) => {
          this.doddle.set(doddle);
        }),
      );
  }

  updateSubscriblyPost(post: SubscriblyPost): Observable<SubscriblyPost> {
    const payload: Partial<SubscriblyPost> = {
      content: post.content,
      status: post.status,
      mediaIds: post.mediaItems.map((media) => media.id),
      type: post.type,
      ...(isValid(post.publishAt) && {
        publishAt: new Date(post.publishAt).toISOString(),
      }),
      ...(post.subChannelId && {
        subChannelId: post.subChannelId,
      }),
    };
    return this.postApiService
      .updateSubscriblyPost(post.id, removeEmptyAttributes(payload))
      .pipe(
        switchMap((post) => {
          // If it's the first update, set the status to draft and update the doddle.
          // PatchPosts flag set to true will update all posts at the same time.
          if (this.isFirstUpdate()) {
            this.isFirstUpdate.set(false);
            this.status.setValue(EPostState.draft, { emitEvent: false });
            this.changeAllPostsFormStatus(EPostState.draft);
            this.postService
              .updateDoddle(
                this.doddle()!.id,
                { status: this.status.value },
                true,
              )
              .subscribe((doddle) => {
                this.doddle.set(doddle);
              });
            return of(post);
          }
          return of(post);
        }),
      );
  }

  updateDoddleAndPatchPosts(
    doddleId: string,
    payload: Partial<Doddle>,
  ): Observable<Doddle> {
    return this.postService.updateDoddle(doddleId, payload, true).pipe(
      tap((doddle) => {
        if (this.isFirstUpdate()) {
          this.isFirstUpdate.set(false);
          this.status.setValue(EPostState.draft, { emitEvent: false });
          this.changeAllPostsFormStatus(EPostState.draft);

          return this.postService.updateDoddle(
            this.doddle()!.id,
            { status: this.status.value },
            true,
          );
        }
        return of(doddle);
      }),
    );
  }

  duplicateDoddle() {
    const accountId = this.workspaceService.activeWorkspace().accountId;
    const workspaceId = this.workspaceService.activeWorkspace().id;

    this.router.navigate(
      [`/account/${accountId}/workspace/${workspaceId}/doddle/new`],
      {
        relativeTo: this.activatedRoute,
        replaceUrl: true,
        state: {
          doddle: this.doddle(),
        },
        queryParams: {
          duplicate: true,
        },
      },
    );
  }

  sendToPublish(): Observable<Doddle> {
    return this.postService.updateDoddle(
      this.doddle()!.id,
      { status: EPostState.scheduled },
      true,
    );
  }

  setActiveChannel(channel: Channel | null): void {
    this.activeChannel.set(channel);
  }

  addPost(postForm: FormGroup): void {
    this.posts.update((posts) => [...posts, postForm]);
  }

  removePost(postId: string): void {
    this.posts.update((posts) =>
      posts.filter((post) => post.get('id')?.value !== postId),
    );
  }

  isPostValid(): boolean {
    const publishDate = this.currentPublishDate();
    return (
      this.posts()
        .filter((post) => post.value.status !== EPostState.paused)
        .every((post) => post.value.validationErrors?.length === 0) &&
      Boolean(
        this.posts().filter((post) => post.value.status !== EPostState.paused)
          .length,
      ) &&
      publishDate !== null &&
      publishDate > new Date()
    );
  }

  isDisabled(): boolean {
    return (
      this.doddle()?.status === EPostState.published ||
      this.doddle()?.status === EPostState.failed
    );
  }

  changeAllPostsFormStatus(status: EPostState): void {
    this.posts.update((posts) =>
      posts.map((post) => {
        // Keep per-channel paused posts untouched. The first save after open
        // (isFirstUpdate) would otherwise set every form to draft and flip all
        // channel toggles on, e.g. when only one paused channel is resumed.
        if (post.get('status')?.value === EPostState.paused) {
          return post;
        }
        post.patchValue({ status });
        return post;
      }),
    );
  }

  reset(): void {
    this.posts.set([]);
    this.isEditMode.set(false);
    this.doddle.set(null);
    this.activeChannel.set(null);
    this.currentPublishDate.set(null);
    this.doddleForm.reset();
    this.isLoading.set(false);
    this.isFirstUpdate.set(true);
  }
}
